// ─────────────────────────────────────────────
// KLOTHIQ — Accounts Overview Tab (TASK-FE-007)
// ─────────────────────────────────────────────

import {
  Landmark,
  Coins,
  TrendingUp,
  ShoppingCart,
  ShoppingBag,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Users,
  PieChart as PieIcon,
  BarChart3,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useOutletContext } from 'react-router-dom';
import { useAccountsSummary } from '@/hooks/useAccounts';
import { useCashAccounts, splitBalances } from '@/hooks/useCash';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { formatINR, formatDisplayDate } from '@/utils/cashbookCalc';
import type { AccountsContextValue } from '../AccountsLayout';

interface AccountsOverviewTabProps {
  from?: string | null;
  to?: string | null;
  onOpenRecordCapital?: () => void;
}

const CATEGORY_COLORS = [
  '#0d9488', // teal-600
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#ec4899', // pink-500
  '#8b5cf6', // purple-500
  '#10b981', // emerald-500
  '#6366f1', // indigo-500
  '#f97316', // orange-500
  '#64748b', // slate-500
];

export default function AccountsOverviewTab(props: AccountsOverviewTabProps) {
  const ctx = useOutletContext<AccountsContextValue | undefined>();
  const from = props.from !== undefined ? props.from : ctx?.from ?? null;
  const to = props.to !== undefined ? props.to : ctx?.to ?? null;
  const onOpenRecordCapital = props.onOpenRecordCapital ?? ctx?.openCapitalModal;
  const { data: summary, isLoading, error, refetch } = useAccountsSummary(from, to);
  const { data: accounts } = useCashAccounts();

  if (isLoading) return <LoadingState message="Loading accounts summary…" />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!summary) return null;

  const isReconciled =
    Math.abs(Number(summary.cash_in_hand) - Number(summary.ledger_closing_all_time)) < 0.01;
  const discrepancy = Number(summary.cash_in_hand) - Number(summary.ledger_closing_all_time);

  // Total available KLOTHIQ Bank balance (FR-021, FR-024)
  const { bankBalance } = splitBalances(accounts);

  const kpis: Array<{
    title: string;
    value: string;
    subtext: string;
    icon: React.ReactNode;
    bg: string;
  }> = [
    {
      title: 'Capital Contributed',
      value: formatINR(summary.capital_total),
      subtext: 'Partner equity · Tracked separate from bank',
      icon: <Coins className="h-4 w-4 text-primary" />,
      bg: 'bg-primary/10 text-primary',
    },
    {
      title: 'Net Operating Flow',
      value: formatINR(summary.net_operating),
      subtext: 'Sales − Purchases − Refunds − Expenses',
      icon: <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
      bg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    },
    {
      title: 'Purchases Paid',
      value: formatINR(summary.purchases_paid),
      subtext: 'Supplier inventory disbursements',
      icon: <ShoppingBag className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
      bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      title: 'Operating Expenses',
      value: formatINR(summary.expenses),
      subtext: 'Store, courier, staff & utilities',
      icon: <Receipt className="h-4 w-4 text-rose-600 dark:text-rose-400" />,
      bg: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    },
    {
      title: 'Accounts Receivable',
      value: formatINR(summary.receivables),
      subtext: 'Pending sale dues (excluding returns)',
      icon: <ArrowDownRight className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />,
      bg: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    },
    {
      title: 'Accounts Payable',
      value: formatINR(summary.payables),
      subtext: 'Current · tracked supplier balances due',
      icon: <ArrowUpRight className="h-4 w-4 text-orange-600 dark:text-orange-400" />,
      bg: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Reconciliation Warning Banner (BR-002) */}
      {!isReconciled && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-900 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-xs">
            <p className="font-semibold text-sm">Ledger Reconciliation Discrepancy</p>
            <p className="mt-0.5 text-muted-foreground">
              Total account balance ({formatINR(summary.cash_in_hand)}) does not match the lifetime
              transaction ledger closing balance ({formatINR(summary.ledger_closing_all_time)}).
              Difference:{' '}
              <span className="font-mono font-medium text-foreground">
                {discrepancy > 0 ? `+${formatINR(discrepancy)}` : formatINR(discrepancy)}
              </span>
              . Consider recording a cash adjustment or reviewing voided entries.
            </p>
          </div>
        </div>
      )}

      {/* Hero Financial Cards (FR-021, FR-024) */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* KLOTHIQ Bank — Total Available Balance */}
        <div
          role={ctx?.openBankBalanceModal ? 'button' : undefined}
          tabIndex={ctx?.openBankBalanceModal ? 0 : undefined}
          onClick={ctx?.openBankBalanceModal ? () => ctx.openBankBalanceModal() : undefined}
          onKeyDown={(e) => {
            if (ctx?.openBankBalanceModal && (e.key === 'Enter' || e.key === ' ')) ctx.openBankBalanceModal();
          }}
          className={`obsidian-surface rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-card to-card p-5 shadow-xs transition-all ${
            ctx?.openBankBalanceModal ? 'obsidian-interactive cursor-pointer hover:border-emerald-500/60 hover:shadow-sm' : ''
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Total KLOTHIQ Bank Balance
            </span>
            <div className="rounded-lg bg-emerald-500/15 p-1.5 text-emerald-600 dark:text-emerald-400">
              <Landmark className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 text-2xl font-extrabold tracking-tight text-foreground">
            {formatINR(bankBalance)}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              Total available funds (includes customer sales &amp; collections)
            </p>
            {ctx?.openBankBalanceModal && (
              <span className="shrink-0 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                Update →
              </span>
            )}
          </div>
        </div>

        {/* Sales Collected — Included in Bank Balance */}
        <div className="obsidian-surface rounded-xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 via-card to-card p-5 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Sales Revenue Collected
            </span>
            <div className="rounded-lg bg-teal-500/15 p-1.5 text-teal-600 dark:text-teal-400">
              <ShoppingCart className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 text-2xl font-extrabold tracking-tight text-foreground">
            {formatINR(summary.sales_collected)}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Customer payments collected in period · Included in bank balance
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, idx) => (
          <div
            key={idx}
            className="obsidian-surface flex flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-xs transition-shadow hover:shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">{kpi.title}</span>
              <div className={`rounded-lg p-1.5 ${kpi.bg}`}>{kpi.icon}</div>
            </div>
            <div className="mt-2">
              <div className="text-lg font-bold tracking-tight text-foreground sm:text-xl">
                {kpi.value}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground line-clamp-1">{kpi.subtext}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Cash Flow Trends & Expenses Breakdown */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Cash Flow Trend Chart */}
        <div className="obsidian-surface lg:col-span-2 rounded-xl border border-border bg-card p-5 shadow-xs">
          <div className="flex items-center justify-between pb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                Cash Flow & Balance Trend
              </h3>
              <p className="text-xs text-muted-foreground">
                Inflow vs Outflow with running cash balance
              </p>
            </div>
            <span className="rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground capitalize">
              {summary.trend.bucket}ly resolution
            </span>
          </div>

          <div className="h-64 w-full">
            {summary.trend.series.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.trend.series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
                  <XAxis
                    dataKey="bucket"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => {
                      if (val.length === 10) {
                        const [, m, d] = val.split('-');
                        return `${d}/${m}`;
                      }
                      return val;
                    }}
                    stroke="currentColor"
                    className="text-muted-foreground"
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                    stroke="currentColor"
                    className="text-muted-foreground"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      borderColor: 'hsl(var(--border))',
                      borderRadius: '0.5rem',
                      fontSize: '12px',
                    }}
                    formatter={(val: any) => [formatINR(val), '']}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                  <Bar dataKey="income" name="Inflow (₹)" fill="#0d9488" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Outflow (₹)" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    name="Cash Balance (₹)"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                No cash transactions in this period
              </div>
            )}
          </div>
        </div>

        {/* Expense Category Breakdown */}
        <div className="obsidian-surface rounded-xl border border-border bg-card p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <PieIcon className="h-4 w-4 text-primary" />
                Expense Breakdown
              </h3>
              <span className="text-xs font-semibold text-foreground">
                {formatINR(summary.expenses)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Expenses by category</p>

            {summary.categories.length > 0 ? (
              <>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.categories}
                        dataKey="amount"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={36}
                        outerRadius={60}
                        paddingAngle={2}
                      >
                        {summary.categories.map((_, i) => (
                          <Cell
                            key={`cell-${i}`}
                            fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          borderColor: 'hsl(var(--border))',
                          borderRadius: '0.5rem',
                          fontSize: '12px',
                        }}
                        formatter={(val: any) => [formatINR(val), '']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-3 space-y-1.5 max-h-36 overflow-y-auto pr-1">
                  {summary.categories.map((c, i) => (
                    <div key={c.category_id || i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                        />
                        <span className="truncate max-w-[120px] text-foreground">{c.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-foreground">{formatINR(c.amount)}</span>
                        <span className="ml-1 text-[10px] text-muted-foreground">({c.pct}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
                No expense entries in this period
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Partner Equity & Capital Contributions Summary */}
      <div className="obsidian-surface rounded-xl border border-border bg-card p-5 shadow-xs">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Partner Capital & Equity
            </h3>
            <p className="text-xs text-muted-foreground">
              Total invested capital: <span className="font-semibold text-foreground">{formatINR(summary.capital_total)}</span>
            </p>
          </div>
          {onOpenRecordCapital && (
            <button
              type="button"
              onClick={() => onOpenRecordCapital()}
              className="rounded-md bg-secondary px-3 py-1.5 text-xs font-medium text-secondary-foreground hover:bg-secondary/80"
            >
              + Deposit Capital
            </button>
          )}
        </div>

        {summary.partners.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border/80 bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="py-2.5 px-3 font-medium">Partner</th>
                  <th className="py-2.5 px-3 font-medium">Agreed Share</th>
                  <th className="py-2.5 px-3 font-medium text-right">Contributed</th>
                  <th className="py-2.5 px-3 font-medium text-right">% of Capital</th>
                  <th className="py-2.5 px-3 font-medium text-center">Entries</th>
                  <th className="py-2.5 px-3 font-medium text-right">Last Deposit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {summary.partners.map((p) => (
                  <tr key={p.partner_id} className="hover:bg-muted/20">
                    <td className="py-2.5 px-3 font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        <span>{p.name}</span>
                        {!p.is_active && (
                          <span className="rounded bg-muted px-1.5 py-0.2 text-[10px] text-muted-foreground">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground">{p.share_percent}%</td>
                    <td className="py-2.5 px-3 text-right font-medium text-foreground">
                      {formatINR(p.contributed)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-muted-foreground">
                      {p.pct_of_capital}%
                    </td>
                    <td className="py-2.5 px-3 text-center text-muted-foreground">{p.entries}</td>
                    <td className="py-2.5 px-3 text-right text-muted-foreground">
                      {p.last_date ? formatDisplayDate(p.last_date) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-xs text-muted-foreground">
            No partner capital records found
          </div>
        )}
      </div>
    </div>
  );
}
